import {
    ExpenseSubMode,
    RevenueSubMode,
    ScheduleApplicationStatus,
    ScheduleBillingStatus,
    ScheduleLifecycleFilterStatus,
    ScheduleSourceType,
    ScheduleType,
} from "@common/types/domains/accounting/schedule";

export const REVENUE_SUB_MODE_LABELS: Record<RevenueSubMode, string> = {
    [RevenueSubMode.deferred]: "Deferred revenue",
    [RevenueSubMode.accrued]: "Accrued revenue",
};

export const EXPENSE_SUB_MODE_LABELS: Record<ExpenseSubMode, string> = {
    [ExpenseSubMode.prepaid]: "Prepaid expenses",
    [ExpenseSubMode.accrued]: "Accrued expenses",
};

function matchesOptionalFilter<T>(value: T | null, selected: T[] | undefined) {
    if (!selected || selected.length === 0) {
        return true;
    }
    return value !== null && selected.includes(value);
}

/** An empty filter matches every row; a template with no sub-mode never matches a set one. */
export function matchesRevenueSubModeFilter(
    row: { revenueSubMode: RevenueSubMode | null },
    revenueSubModes: RevenueSubMode[] | undefined,
): boolean {
    return matchesOptionalFilter(row.revenueSubMode, revenueSubModes);
}

export function matchesExpenseSubModeFilter(
    row: { expenseSubMode: ExpenseSubMode | null },
    expenseSubModes: ExpenseSubMode[] | undefined,
): boolean {
    return matchesOptionalFilter(row.expenseSubMode, expenseSubModes);
}

export function matchesScheduleTypeFilter(
    row: { scheduleType: ScheduleType },
    scheduleTypes: ScheduleType[] | undefined,
): boolean {
    return matchesOptionalFilter(row.scheduleType, scheduleTypes);
}

export const SCHEDULE_SOURCE_TYPE_LABELS: Record<ScheduleSourceType, string> = {
    [ScheduleSourceType.transaction]: "Transaction",
    [ScheduleSourceType.invoice]: "Invoice",
    [ScheduleSourceType.bill]: "Bill",
    [ScheduleSourceType.manual]: "Manual",
};

export function matchesScheduleLifecycleFilterStatus(
    row: { status: ScheduleApplicationStatus; isModified: boolean },
    filterStatus: ScheduleLifecycleFilterStatus,
): boolean {
    switch (filterStatus) {
        case ScheduleLifecycleFilterStatus.active:
            return row.status === ScheduleApplicationStatus.active;
        case ScheduleLifecycleFilterStatus.modified:
            return (
                row.isModified &&
                row.status === ScheduleApplicationStatus.active
            );
        case ScheduleLifecycleFilterStatus.paused:
            return row.status === ScheduleApplicationStatus.paused;
        case ScheduleLifecycleFilterStatus.stopped:
            return row.status === ScheduleApplicationStatus.stopped;
        case ScheduleLifecycleFilterStatus.completed:
            return row.status === ScheduleApplicationStatus.completed;
        case ScheduleLifecycleFilterStatus.unwound:
            return row.status === ScheduleApplicationStatus.unwound;
    }
}

export const SCHEDULE_BILLING_STATUS_LABELS: Record<
    ScheduleBillingStatus,
    string
> = {
    [ScheduleBillingStatus.unbilled]: "Unbilled",
    [ScheduleBillingStatus.partiallyBilled]: "Partially billed",
    [ScheduleBillingStatus.billed]: "Billed",
};

/** An empty filter matches every row; a schedule with no billing status never matches a set one. */
export function matchesScheduleBillingStatusFilter(
    row: { billingStatus: ScheduleBillingStatus | null },
    billingStatuses: ScheduleBillingStatus[] | undefined,
): boolean {
    if (!billingStatuses || billingStatuses.length === 0) {
        return true;
    }
    return (
        row.billingStatus !== null &&
        billingStatuses.includes(row.billingStatus)
    );
}
